/**
 * <TimerBar> — une barre fine qui se remplit en une durée donnée, une fois (atom).
 *
 * Elle montre que la suite arrive seule : l'écran qui la pose enchaîne à l'échéance, et la barre
 * pleine dit « c'est maintenant ». Elle ne tient pas le minuteur : c'est l'écran qui décide quand
 * il enchaîne, avec la même durée. Le remplissage est linéaire : c'est du temps qui passe, pas une
 * entrée (la courbe du livre ralentirait la fin, et la barre semblerait en retard sur l'écran).
 *
 * En mouvement réduit, elle n'est pas montée : rien ne bouge, l'écran enchaîne quand même, et ce
 * qui dit la suite (ses mots) reste à côté d'elle.
 *
 * Elle ne prend que la couleur (`color`, l'univers par défaut) ; l'épaisseur est sa géométrie.
 *
 * `cover` : elle couvre ce qui la porte, sans piste, sur toute sa hauteur. C'est le remplissage
 * de la face d'un bouton qui se presse seul (`<CountdownButton>`, posé par `underlay`) : le bouton
 * se remplit, comme l'ancien `TouchableRippleM`, au lieu d'une barre posée à part.
 */
import React, { useEffect, useRef } from "react"
import { Animated, Easing, Platform, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { useReduceMotion } from "../../hooks/useReduceMotion"
import { type SemanticColorKey } from "../../theme/tokens/colors"

/** Fine : un fil sous ce qui l'annonce, pas une jauge. */
const TIMER_BAR_HEIGHT = 4

export type TimerBarProps = {
    /** Le temps qu'elle met à se remplir, en millisecondes. Une autre valeur la fait repartir de zéro. */
    durationMs: number
    /** La couleur du remplissage — `role` par défaut, la couleur de l'univers. */
    color?: SemanticColorKey
    /** Elle couvre ce qui la porte : ni piste, ni épaisseur à elle. Défaut `false`. */
    cover?: boolean
    testID?: string
}

export function TimerBar(props: TimerBarProps) {
    const { durationMs, color = "role", cover = false, testID } = props

    const reduceMotion = useReduceMotion()
    const progress = useRef(new Animated.Value(0)).current

    useEffect(() => {
        if (reduceMotion) return
        progress.setValue(0)
        const fill = Animated.timing(progress, {
            toValue: 1,
            duration: durationMs,
            easing: Easing.linear,
            // react-native-web n'a pas de pilote natif ; y prétendre ne fait qu'avertir.
            useNativeDriver: Platform.OS !== "web",
        })
        fill.start()
        return () => fill.stop()
    }, [reduceMotion, durationMs, progress])

    if (reduceMotion) return null

    return (
        <View
            style={cover ? styles.cover : styles.track}
            testID={testID}
            accessibilityElementsHidden
            importantForAccessibility="no"
        >
            <Animated.View style={[styles.fill(color), { transform: [{ scaleX: progress }] }]} />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    track: {
        width: "100%",
        height: TIMER_BAR_HEIGHT,
        borderRadius: theme.radius.full,
        // Le contour du livre : la piste se lit sur la page comme sur une carte, en clair et sur la nuit.
        backgroundColor: theme.colors.border,
        overflow: "hidden",
    },
    /** Sur une face : toute la place, rognée par ce qui la porte. */
    cover: {
        width: "100%",
        height: "100%",
        overflow: "hidden",
    },
    /** Le remplissage grandit depuis la gauche ; la piste arrondie le rogne. */
    fill: (color: SemanticColorKey) => ({
        flex: 1,
        backgroundColor: theme.colors[color],
        transformOrigin: "left",
    }),
}))
